import { useMemo } from 'react';
import type { ActiveRemediationRunSummary } from '@libs/types';

import { RemediationRunStatus } from '@libs/constants';

import { useActiveRemediationRuns } from '../api/remediation-api';

/**
 * Every status a row's state is derived from — the active ones plus the finished
 * ones. The endpoint returns only active runs unless asked, which is right for
 * the dashboard and wrong here: a completed run is exactly what proves an
 * assessment has been remediated.
 *
 * FAILED belongs for the same reason COMPLETED does. Without it a run that broke
 * vanished from the table, the row fell back to "not yet remediated", and the
 * user was told nothing had happened when their run had died.
 *
 * DISCARDED is deliberately excluded: a discarded run applied nothing, so "not
 * yet remediated" is the honest reading of that lineage.
 */
const ROW_STATE_RUN_STATUSES: RemediationRunStatus[] = [
  RemediationRunStatus.PENDING,
  RemediationRunStatus.PROCESSING,
  RemediationRunStatus.READY_FOR_REVIEW,
  RemediationRunStatus.FINALIZING,
  RemediationRunStatus.COMPLETED,
  RemediationRunStatus.FAILED,
];

/**
 * Run statuses that still need something from the user.
 *
 * A run in one of these keeps its lineage even if a later run finished, because
 * the outstanding decision or the failure is what the user has to act on. Keyed
 * on run status rather than the derived row state, which also depends on the
 * assessment the run is matched against and is not known here.
 */
const LIVE_RUN_STATUSES: readonly RemediationRunStatus[] = [
  RemediationRunStatus.PENDING,
  RemediationRunStatus.PROCESSING,
  RemediationRunStatus.FINALIZING,
  RemediationRunStatus.READY_FOR_REVIEW,
];

/** Everything the list knows about one assessment lineage's remediation history. */
export interface LineageRuns {
  /**
   * The run the row's state comes from. A run that still wants something from
   * the user outranks any other on the lineage however recent; between two
   * finished runs the newest wins, so a twice-remediated assessment does not
   * point at its first pass.
   */
  current: ActiveRemediationRunSummary;
  /** Every run on the lineage, newest first — what the history dialog reads. */
  all: ActiveRemediationRunSummary[];
  /**
   * The newest finished run that actually produced something the user kept.
   *
   * "Remediated" is not merely "a run happened": a run can complete having
   * generated nothing — every document rejected, or nothing to change — and an
   * assessment whose remediation produced no documents has not been remediated
   * in any sense the user would recognise.
   */
  produced: ActiveRemediationRunSummary | undefined;
}

/**
 * The tenant's remediation runs, indexed by assessment lineage.
 *
 * KEYED BY LINEAGE ROOT, NOT ASSESSMENT ID. A run points at the exact version it
 * started from, and finalizing it forks version N+1 — which is the version the
 * list actually shows. So matching runs to rows by assessment id finds nothing
 * once a run finishes, and a remediated assessment reads as "not yet
 * remediated". The root is what both ends can agree on.
 *
 * One pass over the runs rather than three. `current`, `all` and `produced` are
 * three questions about the same lineage, and answering them separately meant
 * three maps that could disagree about which run was newest.
 */
export function useRemediationLineages(): {
  byRoot: Map<string, LineageRuns>;
  isLoading: boolean;
} {
  // `includeItems: false` — this page reads a run's status, lineage and id, and
  // nothing else. Asking for finished runs means asking for every run the tenant
  // has ever completed, and their items would be the bulk of that payload.
  const { data, isLoading } = useActiveRemediationRuns(true, ROW_STATE_RUN_STATUSES, false);

  const byRoot = useMemo(() => {
    const lineages = new Map<string, LineageRuns>();

    // Newest first from the endpoint, which is what lets every "first match
    // wins" below mean "the latest one".
    for (const run of data?.runs ?? []) {
      const lineage = lineages.get(run.rootAssessmentId);
      if (!lineage) {
        lineages.set(run.rootAssessmentId, {
          current: run,
          all: [run],
          produced: hasKeptOutput(run) ? run : undefined,
        });
        continue;
      }

      lineage.all.push(run);
      if (!lineage.produced && hasKeptOutput(run)) lineage.produced = run;

      // A live run outranks a finished one however recent; otherwise the run
      // already held is the newer of the two and stays.
      if (!LIVE_RUN_STATUSES.includes(lineage.current.status) && isLive(run)) {
        lineage.current = run;
      }
    }

    return lineages;
  }, [data]);

  return { byRoot, isLoading };
}

const isLive = (run: ActiveRemediationRunSummary): boolean =>
  LIVE_RUN_STATUSES.includes(run.status);

const hasKeptOutput = (run: ActiveRemediationRunSummary): boolean =>
  run.status === RemediationRunStatus.COMPLETED &&
  run.itemCounts.generated + run.itemCounts.remediated > 0;
